import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, MessageSquare, ChevronDown, ChevronRight, X } from 'lucide-react';
import { LogOutcomeDialog } from './LogOutcomeDialog';
import { OutcomeHistory } from './OutcomeHistory';
import { outcomeLabel, xhrJson } from './outcomeApi';

const STATUS = {
  active: { label: 'In queue pool', cls: 'bg-green-100 text-green-700 border-green-200' },
  cooldown: { label: 'On cooldown', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  cold: { label: 'Cold', cls: 'bg-gray-200 text-gray-800 border-gray-300' },
  not_interested: { label: 'Not interested', cls: 'bg-red-100 text-red-700 border-red-200' },
  dormant: { label: 'Dormant', cls: 'bg-red-100 text-red-700 border-red-200' },
};

function ResultRow({ p, onLog, refreshKey, onChanged }) {
  const [open, setOpen] = useState(false);
  const st = STATUS[p.queue_status] || STATUS.active;
  return (
    <div className="p-3" data-testid={`search-result-${p.user_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <button onClick={() => setOpen(o => !o)} data-testid={`search-expand-${p.user_id}`} className="mt-0.5">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Link to={`/admin/partners/${p.user_id}`} className="font-semibold hover:underline">{p.name || '—'}</Link>
              <Badge variant="outline" className={`${st.cls} border`} data-testid={`search-status-${p.user_id}`}>
                {st.label}{p.cooldown_until && ` until ${new Date(p.cooldown_until).toLocaleDateString()}`}
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-3">
              <span>{p.email}</span>{p.phone && <span>📞 {p.phone}</span>}{p.city && <span>📍 {p.city}</span>}
            </div>
            {p.latest_outcome && (
              <div className="text-xs mt-1 text-muted-foreground">
                Last: <strong>{outcomeLabel(p.latest_outcome.outcome_type)}</strong>
                {p.latest_outcome.note && <> — "{p.latest_outcome.note}"</>} · {new Date(p.latest_outcome.created_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
        <Button size="sm" onClick={() => onLog(p)} data-testid={`search-log-outcome-${p.user_id}`} className="bg-orange-500 hover:bg-orange-600 text-white">
          <MessageSquare className="w-4 h-4 mr-1.5" /> Log outcome
        </Button>
      </div>
      {open && (
        <div className="mt-2 ml-6 bg-muted/30 rounded p-3">
          <OutcomeHistory partnerId={p.user_id} refreshKey={refreshKey} onChanged={onChanged} />
        </div>
      )}
    </div>
  );
}

export function PartnerSearchLog({ onLogged }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const search = (term) => {
    if (term.trim().length < 2) { setResults(null); return; }
    setLoading(true);
    xhrJson('GET', `/admin/reengagement/search?q=${encodeURIComponent(term.trim())}`, null, (s, d) => {
      setLoading(false);
      if (s === 200) setResults(d.partners || []);
    });
  };

  useEffect(() => {
    const t = setTimeout(() => search(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const changed = () => { search(q); setRefreshKey(k => k + 1); onLogged?.(); };

  return (
    <Card className="p-3 mb-4" data-testid="partner-search-card">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={e => setQ(e.target.value)} className="pl-9 pr-9"
          placeholder="Find any partner by name, email, phone or code to log a comment / outcome…"
          data-testid="partner-search-input" />
        {q && (
          <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="partner-search-clear">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {loading && <div className="text-xs text-muted-foreground mt-2">Searching…</div>}
      {results && (
        results.length === 0
          ? <div className="text-xs text-muted-foreground mt-2" data-testid="partner-search-empty">No partners found.</div>
          : <div className="divide-y mt-2 border rounded-md max-h-[420px] overflow-y-auto" data-testid="partner-search-results">
              {results.map(p => <ResultRow key={p.user_id} p={p} onLog={setSelected} refreshKey={refreshKey} onChanged={changed} />)}
            </div>
      )}
      <LogOutcomeDialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)} partner={selected} onSaved={changed} />
    </Card>
  );
}
